import React, { useState, useEffect, useMemo } from 'react';
import { CubeIcon, PlusIcon, PencilSquareIcon, TrashIcon } from '@heroicons/react/24/outline';
import { useQuery, useMutation, usePagination, useDebounce } from '../../hooks';
import { productsAPI, categoriesAPI } from '../../services/api';
import {
  Card, SectionHeader, SearchInput, Button, Badge, StatusBadge,
  Modal, Input, Select, Textarea, Pagination, LoadingPage,
  EmptyState, ConfirmDialog,
} from '../../components/common/UI';
import { useForm } from 'react-hook-form';
import { format } from 'date-fns';
import toast from 'react-hot-toast';

const BASE_URL = (process.env.REACT_APP_BASE_URL || 'http://localhost:5000').replace(/\/$/, '');

const PRODUCT_TYPES = ['physical', 'digital', 'service', 'subscription'];
const PRODUCT_CATEGORIES = ['standard', 'promo'];  // ✅ NEW
const TAX_RATES = [0, 5, 12, 18, 28];
const TABS = ['Basic Info', 'Pricing', 'Variants', 'Images', 'Attributes', 'Inventory', 'Settings'];

// ── Normalize Category Responses (Flat, Tree, Rows, Data) ──
const normalizeCategories = (raw) => {
  const flat = [];
  const visited = new Set();

  const extract = (item, inheritedParentId = null) => {
    if (!item || typeof item !== 'object') return;

    const rawId = item.id ?? item._id ?? item.category_id;
    if (rawId === undefined || rawId === null) return;

    const strId = String(rawId);
    if (visited.has(strId)) return;
    visited.add(strId);

    const rawPid =
      item.parent_id ??
      item.parentId ??
      item.parent_category_id ??
      (item.parent && typeof item.parent === 'object' ? (item.parent.id ?? item.parent._id) : item.parent) ??
      item.parent_slug ??
      item.parent_name ??
      inheritedParentId;

    const isRoot =
      rawPid === null ||
      rawPid === undefined ||
      rawPid === '' ||
      rawPid === 0 ||
      rawPid === '0' ||
      rawPid === 'null' ||
      rawPid === 'undefined';

    const finalParentId = !isRoot ? rawPid : null;

    const childList =
      item.children ||
      item.subcategories ||
      item.sub_categories ||
      item.subCategories ||
      item.child_categories ||
      item.childCategories ||
      item.sub_sub_categories;

    const {
      children,
      subcategories,
      sub_categories,
      subCategories,
      child_categories,
      childCategories,
      sub_sub_categories,
      ...cleanItem
    } = item;

    flat.push({
      ...cleanItem,
      id: rawId,
      parent_id: finalParentId,
    });

    if (Array.isArray(childList)) {
      childList.forEach((child) => {
        extract(child, rawId);
      });
    }
  };

  const list = Array.isArray(raw)
    ? raw
    : Array.isArray(raw?.rows)
    ? raw.rows
    : Array.isArray(raw?.data)
    ? raw.data
    : Array.isArray(raw?.categories)
    ? raw.categories
    : Array.isArray(raw?.items)
    ? raw.items
    : [];

  list.forEach((item) => extract(item, null));
  return flat;
};

// ── Build Category Hierarchy Tree (Root -> Sub -> Sub-sub) ──
const buildCategoryTree = (categories = []) => {
  const mapById = {};
  const mapBySlug = {};
  const mapByName = {};
  const roots = [];

  categories.forEach((cat) => {
    const strId = String(cat.id);
    const node = { ...cat, children: [] };
    mapById[strId] = node;
    if (cat.slug) mapBySlug[String(cat.slug).toLowerCase().trim()] = node;
    if (cat.name) mapByName[String(cat.name).toLowerCase().trim()] = node;
  });

  categories.forEach((cat) => {
    const rawPid = cat.parent_id;
    const strId = String(cat.id);
    const node = mapById[strId];

    const isRoot =
      rawPid === null ||
      rawPid === undefined ||
      rawPid === '' ||
      rawPid === 0 ||
      rawPid === '0' ||
      rawPid === 'null' ||
      rawPid === 'undefined';

    if (!isRoot) {
      const pidStr = String(rawPid);
      const pidLower = pidStr.toLowerCase().trim();

      const parentNode =
        mapById[pidStr] ||
        mapBySlug[pidLower] ||
        mapByName[pidLower];

      if (parentNode && String(parentNode.id) !== strId) {
        if (!parentNode.children.some((c) => String(c.id) === strId)) {
          parentNode.children.push(node);
        }
        return;
      }
    }

    roots.push(node);
  });

  const sortTree = (nodes) => {
    nodes.sort((a, b) => {
      const orderA = Number(a.sort_order) || 0;
      const orderB = Number(b.sort_order) || 0;
      if (orderA !== orderB) return orderA - orderB;
      return (a.name || '').localeCompare(b.name || '');
    });
    nodes.forEach((n) => sortTree(n.children));
  };

  sortTree(roots);
  return { roots, map: mapById };
};

const parseTags = (tags) => {
  if (!tags) return '';
  if (Array.isArray(tags)) return tags.join(', ');
  try { return JSON.parse(tags).join(', '); } catch { return tags; }
};

const ToggleField = ({ label, hint, checked, onChange }) => (
  <div className="flex items-center justify-between py-2">
    <div>
      <p className="text-sm font-medium text-surface-800">{label}</p>
      {hint && <p className="text-xs text-surface-400">{hint}</p>}
    </div>
    <button
      type="button"
      onClick={() => onChange(!checked)}
      className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors flex-shrink-0
        ${checked ? 'bg-surface-900' : 'bg-surface-200'}`}
    >
      <span className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform
        ${checked ? 'translate-x-4' : 'translate-x-0.5'}`} />
    </button>
  </div>
);

const TabBar = ({ tabs, active, onChange }) => (
  <div className="flex border-b border-surface-200 overflow-x-auto flex-shrink-0">
    {tabs.map((t, i) => (
      <button
        key={t} type="button" onClick={() => onChange(i)}
        className={`px-3 py-2.5 text-xs font-medium whitespace-nowrap border-b-2 transition-colors
          ${active === i
            ? 'border-surface-900 text-surface-900'
            : 'border-transparent text-surface-400 hover:text-surface-600'}`}
      >
        {t}
      </button>
    ))}
  </div>
);


const ProductForm = ({ product, categories, onSubmit, loading, business }) => {
  const [tab, setTab] = useState(0);

  const [variants, setVariants] = useState(() =>
    product?.variants?.length
      ? product.variants.map(v => ({
        id: v.id || '',
        name: v.name || '',
        sku: v.sku || '',
        price: v.price || '',
        sale_price: v.sale_price || '',
        cost_price: v.cost_price || '',
        options: typeof v.options === 'string' ? v.options : JSON.stringify(v.options || {}),
        images: Array.isArray(v.images) ? v.images : [],
        localFiles: []
      }))
      : [{ name: '', sku: '', price: '', sale_price: '', cost_price: '', options: '{}', images: [], localFiles: [] }]
  );

  const [images, setImages] = useState(() =>
    product?.images?.length
      ? product.images.map(i => ({ url: i.url || '', alt_text: i.alt_text || '', is_primary: i.is_primary || 0 }))
      : [{ url: '', alt_text: '', is_primary: 1 }]
  );

  const [attributes, setAttributes] = useState(() =>
    product?.attributes?.length
      ? product.attributes.map(a => ({ name: a.name || '', value: a.value || '' }))
      : []
  );

  const [toggles, setToggles] = useState({
    is_active: product ? Boolean(product.is_active) : true,
    is_featured: product ? Boolean(product.is_featured) : false,
    is_returnable: product ? Boolean(product.is_returnable) : true,
    track_inventory: product ? Boolean(product.track_inventory) : true,
    tax_included: product ? Boolean(product.tax_included) : false,
  });
  const setToggle = (key) => (val) => setToggles(prev => ({ ...prev, [key]: val }));

  const { register, handleSubmit, watch, setValue, reset, trigger, formState: { errors, isValid } } = useForm({
    mode: 'onChange',
    defaultValues: product
      ? { ...product, tags: parseTags(product.tags) }
      : {
        product_type: 'physical',
        product_category: 'standard',
        tax_rate: 18,
        currency: 'INR',
        gender: '',
        return_window_days: 7,
        low_stock_alert: 5,
        sort_order: 0,
      },
  });

  useEffect(() => {
    if (product) {
      reset({
        ...product,
        tax_rate: Number(product.tax_rate) || 18,
        stock_qty: product.stock_qty ?? 0,
        reserved_qty: product.reserved_qty ?? 0,
        warehouse_location: product.warehouse_location ?? '',
        low_stock_alert: product.low_stock_alert ?? 5,
        weight_grams: product.weight_grams ?? '',
        tags: parseTags(product.tags),
        product_category: product.product_category || 'standard',
      });
      setToggles({
        is_active: Boolean(product.is_active),
        is_featured: Boolean(product.is_featured),
        is_returnable: Boolean(product.is_returnable),
        track_inventory: Boolean(product.track_inventory),
        tax_included: Boolean(product.tax_included),
      });
      setAttributes(product.attributes?.map(a => ({ name: a.name, value: a.value })) || []);
      setVariants(product.variants?.map(v => ({
        id: v.id,
        name: v.name || '',
        sku: v.sku || '',
        price: v.price || '',
        sale_price: v.sale_price || '',
        cost_price: v.cost_price || '',
        options: typeof v.options === 'string' ? v.options : JSON.stringify(v.options || {}),
        images: Array.isArray(v.images)
          ? v.images.map(img => typeof img === 'string' ? img : (img?.url || '')).filter(Boolean)
          : [],
        localFiles: [],
        _stock_qty: v.inventory?.stock_qty ?? '',
        _reserved_qty: v.inventory?.reserved_qty ?? '',
        _warehouse_location: v.inventory?.warehouse_location ?? '',
      })) || [{ name: '', sku: '', price: '', sale_price: '', cost_price: '', options: '{}', images: [], localFiles: [] }]);
      setImages(
        product.images?.map(i => ({
          url: i.url || i.image_url || i.src || '',
          alt_text: i.alt_text || '',
          is_primary: i.is_primary ?? 0,
        })).filter(i => i.url) || [{ url: '', alt_text: '', is_primary: 1 }]
      );
    }
  }, [product?.id]);

  const selectedType = watch('product_type');
  const selectedCategory = watch('product_category');
  const selectedCatId = watch('category_id');

  const activeCategory = categories?.find(c => String(c.id) === String(selectedCatId));
  const isGenderNeeded = activeCategory
    ? (activeCategory.is_gender_applicable == 1 || activeCategory.is_gender_applicable === true || activeCategory.is_gender_applicable === undefined)
    : true;

  console.log('Category Selection Debug:', {
    id: selectedCatId,
    applicable: activeCategory?.is_gender_applicable,
    isGenderNeeded
  });

  useEffect(() => {
    if (!isGenderNeeded) {
      setValue('gender', '');
    }
  }, [isGenderNeeded, setValue]);

  const handleFormSubmit = (data) => {
    const formData = new FormData();

    Object.keys(data).forEach(key => {
      if (!["attributes", "tags", "images"].includes(key)) {
        formData.append(key, data[key] ?? '');
      }
    });

    formData.set("is_active", toggles.is_active ? 1 : 0);
    formData.set("is_featured", toggles.is_featured ? 1 : 0);
    formData.set("is_returnable", toggles.is_returnable ? 1 : 0);
    formData.set("track_inventory", toggles.track_inventory ? 1 : 0);
    formData.set("tax_included", toggles.tax_included ? 1 : 0);

    // product_category — selectedCategory watch se lena GUARANTEED current value hai
    // data.product_category stale ho sakta hai reset() ke baad, isliye watch use karo
    const finalCategory = selectedCategory || data.product_category || 'standard';
    formData.set("product_category", finalCategory);

    const cleanAttributes = attributes.filter(a => a.name && a.name.trim() !== "");
    formData.set("attributes", JSON.stringify(cleanAttributes));

    // Variants — sirf completely empty rows skip karo
    const BACKEND_BASE = (process.env.REACT_APP_BASE_URL || 'https://ecommercebackend.sanskriti-tech.cloud').replace(/\/$/, '');

    const cleanVariants = variants.filter(v => v.name?.trim() || v.price).map((v, i) => {
      const { localFiles, _stock_qty, _reserved_qty, _warehouse_location, images: varImages, ...rest } = v;

      let options = rest.options;
      if (typeof options === "string") {
        try { options = JSON.parse(options); } catch { options = {}; }
      }
      if (typeof options !== "object" || options === null) options = {};

      if (localFiles?.length) {
        localFiles.forEach((file, fIdx) => {
          formData.append(`variant_${i}_file_${fIdx}`, file);
        });
      }

      // Variant images — URL normalize karo
      const normalizedVarImages = (Array.isArray(varImages) ? varImages : [])
        .map(img => {
          const raw = typeof img === 'string' ? img : (img?.url || '');
          return raw.startsWith(BACKEND_BASE) ? raw.slice(BACKEND_BASE.length) : raw;
        })
        .filter(Boolean);

      // id: existing variant ka id rakho, naye variant ka id send mat karo
      const variantId = rest.id && String(rest.id).trim() !== '' ? rest.id : undefined;
      const { id: _id, ...restWithoutId } = rest;

      return {
        ...restWithoutId,
        ...(variantId ? { id: variantId } : {}),
        options,
        images: normalizedVarImages,
        stock_qty: _stock_qty ?? '',
        reserved_qty: _reserved_qty ?? '',
        warehouse_location: _warehouse_location ?? '',
      };
    });
    formData.set("variants", JSON.stringify(cleanVariants));

    if (data.tags) {
      const tagsArray = data.tags.split(",").map(t => t.trim()).filter(Boolean);
      formData.set("tags", JSON.stringify(tagsArray));
    }

    formData.set("business_id", business?.id || "1");

    formData.set("dimensions_cm", JSON.stringify({
      length: Number(data.length) || 0,
      width: Number(data.width) || 0,
      height: Number(data.height) || 0,
    }));

    // Main product images — existing_images HAMESHA bhejna hai
    // Full URL → relative path normalize karo (backend /uploads/... expect karta hai)
    const hasNewFiles = images.some(img => img.file instanceof File);
    const existingImages = images
      .filter(img => !(img.file instanceof File) && img.url)
      .map(img => {
        let url = img.url || '';
        if (url.startsWith(BACKEND_BASE)) {
          url = url.slice(BACKEND_BASE.length);
        }
        return { url, alt_text: img.alt_text || '', is_primary: img.is_primary ?? 0 };
      });

    formData.set('existing_images', JSON.stringify(existingImages));

    if (hasNewFiles) {
      images.forEach((img) => {
        if (img.file instanceof File) {
          formData.append('images', img.file);
        }
      });
    }

    console.log('=== PRODUCT UPDATE DEBUG ===');
    console.log('product_category:', formData.get('product_category'));
    console.log('existing_images count:', existingImages.length, existingImages);
    console.log('variants:', cleanVariants);
    onSubmit(formData);
  };

  const updateVariant = (i, key, val) =>
    setVariants(prev => prev.map((r, j) => j === i ? { ...r, [key]: val } : r));
  const removeVariant = (i) => setVariants(prev => prev.filter((_, j) => j !== i));
  const addVariant = () =>
    setVariants(prev => [...prev, { name: '', sku: '', price: '', sale_price: '', cost_price: '', options: '{}', images: [], localFiles: [] }]);

  const handleVariantFileChange = (i, files) => {
    const fileArray = Array.from(files);
    setVariants(prev => prev.map((v, j) => j === i ? { ...v, localFiles: [...(v.localFiles || []), ...fileArray] } : v));
  };

  const removeVariantFile = (vIdx, fIdx) => {
    setVariants(prev => prev.map((v, j) => j === vIdx ? { ...v, localFiles: v.localFiles.filter((_, k) => k !== fIdx) } : v));
  };

  const removeExistingVariantImage = (vIdx, imgIdx) => {
    setVariants(prev => prev.map((v, j) => {
      if (j !== vIdx) return v;
      const currentImgs = Array.isArray(v.images) ? v.images : [];
      return {
        ...v,
        images: currentImgs.filter((_, k) => k !== imgIdx)
      };
    }));
  };

  const removeImage = (i) => setImages(prev => prev.filter((_, j) => j !== i));
  const addImage = () => setImages(prev => [...prev, { url: '', alt_text: '', is_primary: 0 }]);

  const updateAttr = (i, key, val) =>
    setAttributes(prev => prev.map((r, j) => j === i ? { ...r, [key]: val } : r));
  const removeAttr = (i) => setAttributes(prev => prev.filter((_, j) => j !== i));
  const addAttr = () => setAttributes(prev => [...prev, { name: '', value: '' }]);

  const renderCategories = (cats, level = 0) => {
    return cats.flatMap((cat) => {
      const prefix = level === 0 ? '📁 ' : level === 1 ? '\u00A0\u00A0\u00A0\u00A0↳ 📂 ' : '\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0↳ 🏷️ ';
      return [
        <option key={cat.id} value={cat.id}>
          {prefix + cat.name}
        </option>,
        ...(cat?.children?.length ? renderCategories(cat.children, level + 1) : [])
      ];
    });
  };

  const handleTabChange = async (targetTab) => {
    // Only validate when moving FORWARD
    if (targetTab > tab) {
      // 1. Validate Form Fields via React Hook Form
      const fieldsByTab = {
        0: ['name', 'slug', 'category_id', 'product_type', 'product_category'],
        1: ['base_price', 'sale_price', 'cost_price', 'tax_rate'],
        5: ['low_stock_alert'],
        6: ['return_window_days'],
      };

      const fields = fieldsByTab[tab] || [];
      if (fields.length > 0) {
        const isTabValid = await trigger(fields);
        if (!isTabValid) {
          toast.error("Please fill all required fields in this section");
          return;
        }
      }

      // 2. Manual Validation for State-based Tabs
      if (tab === 2) {
        // Filter out completely empty variant rows
        const activeVariants = variants.filter(v => {
          return v.name?.trim() || v.price || (typeof v.options === 'string' ? v.options !== '{}' && v.options.trim() !== '' : Object.keys(v.options || {}).length > 0);
        });

        const invalidVariant = activeVariants.some(v => {
          const hasBase = v.name?.trim() && v.price;
          return !hasBase;
        });

        if (invalidVariant) {
          toast.error("Variants must have Name and Price");
          return;
        }
      }

      if (tab === 3) { // Images Tab
        const hasImage = images.some(img => img.file || img.url);
        if (!hasImage) {
          toast.error("Please upload at least one product image");
          return;
        }
      }

      if (tab === 4) { // Attributes Tab
        const hasAttr = attributes.length > 0 && attributes.every(a => a.name?.trim() && a.value?.trim());
        if (!hasAttr) {
          toast.error("Please add at least one attribute (e.g. Material, Origin)");
          return;
        }
      }

      if (tab === 5) { // Inventory Tab
        const lowStock = watch('low_stock_alert');
        if (!lowStock) {
          toast.error("Please set a Low Stock Alert threshold");
          return;
        }

        if (variants.length > 0) {
          const missingVariantStock = variants.some(v => v._stock_qty === '' || v._stock_qty === undefined);
          if (missingVariantStock) {
            toast.error("Please fill stock quantity for all variants");
            return;
          }
        } else {
          const mainStock = watch('stock_qty');
          if (mainStock === '' || mainStock === undefined) {
            toast.error("Please fill the main stock quantity");
            return;
          }
        }
      }
    }
    setTab(targetTab);
  };

  const { roots: categoryTree } = useMemo(() => buildCategoryTree(categories || []), [categories]);

  return (
    <form onSubmit={handleSubmit(handleFormSubmit)}>
      <TabBar tabs={TABS} active={tab} onChange={handleTabChange} />

      <div className="pt-4 space-y-3 min-h-[320px]">

        {tab === 0 && (
          <>
            <Input label="Product Name *" error={errors.name?.message}
              {...register('name', { required: 'Required' })} />
            <Input label="Slug *" error={errors.slug?.message}
              {...register('slug', { required: 'Required' })} />
            <div className="grid grid-cols-2 gap-3">
              <Input label="SKU" {...register('sku')} />
              <Input label="Brand" {...register('brand')} />
            </div>

            <div>
              <p className="label mb-1">Product Category *</p>
              <div className="flex gap-2 flex-wrap">
                {PRODUCT_CATEGORIES.map(c => (
                  <button key={c} type="button"
                    onClick={() => setValue('product_category', c)}
                    className={`px-3 py-1 rounded-full text-xs border capitalize transition-colors
                      ${selectedCategory === c || (!selectedCategory && c === 'standard')
                        ? 'bg-surface-900 text-white border-surface-900'
                        : 'border-surface-200 text-surface-500 hover:border-surface-400'}`}>
                    {c}
                  </button>
                ))}
              </div>
              <input type="hidden" {...register('product_category')} />
            </div>

            <div>
              <p className="label mb-1">Product Type *</p>
              <div className="flex gap-2 flex-wrap">
                {PRODUCT_TYPES.map(t => (
                  <button key={t} type="button"
                    onClick={() => setValue('product_type', t)}
                    className={`px-3 py-1 rounded-full text-xs border capitalize transition-colors
                      ${selectedType === t
                        ? 'bg-surface-900 text-white border-surface-900'
                        : 'border-surface-200 text-surface-500 hover:border-surface-400'}`}>
                    {t}
                  </button>
                ))}
              </div>
              <input type="hidden" {...register('product_type')} />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <Select label="Category *" error={errors.category_id?.message}
                {...register('category_id', { required: 'Required' })}>
                <option value="">— No category —</option>
                {renderCategories(categoryTree)}
              </Select>
              {isGenderNeeded && (
                <Select label="Gender" {...register('gender')}>
                  {['all', 'men', 'women', 'kids', 'unisex'].map(g =>
                    <option key={g} value={g}>{g}</option>)}
                </Select>
              )}
            </div>

            <Input label="Tags (comma separated)" placeholder="5G, flagship, AMOLED"
              {...register('tags')} />
            <Textarea label="Short Description" rows={2} {...register('short_description')} />
            <Textarea label="Full Description" rows={3} {...register('description')} />
          </>
        )}

        {tab === 1 && (
          <>
            <div className="grid grid-cols-3 gap-3">
              <Input label="Cost Price (₹) *" type="number" step="0.01" min="0" max="9999999.99"
                error={errors.cost_price?.message}
                {...register('cost_price', {
                  required: 'Required',
                  min: { value: 0.01, message: 'Must be > 0' },
                  max: { value: 9999999.99, message: 'Price too large' }
                })} />

              <Input label="Base Price (₹) *" type="number" step="0.01" min="0" max="9999999.99"
                error={errors.base_price?.message}
                {...register('base_price', {
                  required: 'Required',
                  max: { value: 9999999.99, message: 'Price too large' },
                  validate: (val) => {
                    const cost = watch('cost_price');
                    if (cost && Number(val) < Number(cost)) return `Must be ≥ Cost (₹${cost})`;
                    return true;
                  }
                })} />

              <Input label="Sale Price (₹) *" type="number" step="0.01" min="0" max="9999999.99"
                error={errors.sale_price?.message}
                {...register('sale_price', {
                  required: 'Required',
                  max: { value: 9999999.99, message: 'Price too large' },
                  validate: (val) => {
                    const base = watch('base_price');
                    const cost = watch('cost_price');
                    if (base && Number(val) > Number(base)) return `Must be ≤ Base (₹${base})`;
                    if (cost && Number(val) < Number(cost)) return `Must be ≥ Cost (₹${cost})`;
                    return true;
                  }
                })} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Select label="Tax Rate (GST)" {...register('tax_rate')}>
                {TAX_RATES.map(r => <option key={r} value={r}>{r}%</option>)}
              </Select>
              <Select label="Currency" {...register('currency')}>
                <option value="INR">INR</option>
                <option value="USD">USD</option>
              </Select>
            </div>
            <ToggleField label="Tax included in price" checked={toggles.tax_included}
              onChange={setToggle('tax_included')} />
            <div className="border-t border-surface-100 pt-3 mt-1">
              <p className="text-xs font-medium text-surface-500 uppercase tracking-wide mb-2">SEO / Meta</p>
              <div className="grid grid-cols-2 gap-3">
                <Input label="Meta Title" {...register('meta_title')} />
                <Input label="Meta Keywords" {...register('meta_keywords')} />
              </div>
              <Textarea label="Meta Description" rows={2} {...register('meta_description')} />
            </div>
          </>
        )}

        {tab === 2 && (
          <div className="space-y-4">
            <p className="text-xs text-surface-400">
              Each row = one variant. Options as JSON e.g. <code>{`{"color":"Black","size":"M"}`}</code>
            </p>
            <div className="overflow-x-auto">
              <div className="min-w-[1100px]">
                <div className="grid grid-cols-[1.2fr_1fr_0.8fr_0.8fr_1.5fr_2.2fr_40px] gap-3 pb-2 border-b border-surface-100 bg-surface-50 p-3 rounded-t-xl">
                  {['Name', 'SKU', 'Base Price', 'Sale Price', 'Options JSON', 'Variant Images', ''].map(h =>
                    <span key={h} className="text-[10px] uppercase tracking-wider font-bold text-surface-500">{h}</span>)}
                </div>
                <div className="divide-y divide-surface-50 border-x border-b border-surface-50 rounded-b-xl bg-white overflow-hidden">
                  {variants.map((v, i) => (
                    <div key={i} className="flex flex-col p-3 hover:bg-surface-50/50 transition-colors">
                      <div className="grid grid-cols-[1.2fr_1fr_0.8fr_0.8fr_1.5fr_2.2fr_40px] gap-3 items-center">
                        <input className="input text-sm" placeholder="Black / 256GB" value={v.name}
                          onChange={e => updateVariant(i, 'name', e.target.value)} />
                        <input className="input text-sm" placeholder="SKU-001" value={v.sku}
                          onChange={e => updateVariant(i, 'sku', e.target.value)} />
                        <input className="input text-sm" type="number" placeholder="Base ₹" value={v.price}
                          onChange={e => updateVariant(i, 'price', e.target.value)} />
                        <input className="input text-sm" type="number" placeholder="Sale ₹" value={v.sale_price || ''}
                          onChange={e => updateVariant(i, 'sale_price', e.target.value)} />
                        <input
                          className="input text-sm font-mono text-[11px]"
                          placeholder='{"color":"Black"}'
                          value={v.options}
                          onChange={e => updateVariant(i, 'options', e.target.value)}
                        />
                        <div className="space-y-2">
                          <input className="input text-sm bg-primary-50/30 border-primary-100 focus:border-primary-500"
                            placeholder="URLs (comma separated)"
                            value={Array.isArray(v.images) ? v.images.map(img => typeof img === 'string' ? img : (img?.url || '')).filter(Boolean).join(', ') : ''}
                            onChange={e => updateVariant(i, 'images', e.target.value.split(',').map(s => s.trim()).filter(Boolean))} />

                          <div className="flex flex-wrap gap-2 items-center">
                            <label className="flex items-center justify-center h-9 px-3 rounded-lg border border-dashed border-primary-200 bg-primary-50/50 text-[10px] font-bold text-primary-600 cursor-pointer hover:bg-primary-100 transition-colors">
                              <PlusIcon className="h-3 w-3 mr-1" /> UPLOAD FILES
                              <input type="file" multiple className="hidden" onChange={e => handleVariantFileChange(i, e.target.files)} />
                            </label>
                            {Array.isArray(v.images) && v.images.map((img, imgIdx) => {
                              const srcUrl = typeof img === 'string' ? img : (img?.url || img?.preview || '');
                              if (!srcUrl) return null;
                              return (
                                <div key={`exist-${imgIdx}`} className="group relative h-9 w-9 rounded-lg bg-surface-100 border border-surface-200 overflow-hidden shadow-sm flex-shrink-0">
                                  <img src={srcUrl} className="h-full w-full object-cover" alt="" />
                                  <button type="button" onClick={() => removeExistingVariantImage(i, imgIdx)}
                                    className="absolute inset-0 flex items-center justify-center bg-danger-500/80 text-white opacity-0 group-hover:opacity-100 transition-opacity">
                                    <TrashIcon className="h-3.5 w-3.5" />
                                  </button>
                                </div>
                              );
                            })}
                            {v.localFiles?.map((f, fIdx) => (
                              <div key={`local-${fIdx}`} className="group relative h-9 w-9 rounded-lg bg-surface-100 border border-surface-200 overflow-hidden shadow-sm flex-shrink-0">
                                <img src={URL.createObjectURL(f)} className="h-full w-full object-cover" alt="" />
                                <button type="button" onClick={() => removeVariantFile(i, fIdx)}
                                  className="absolute inset-0 flex items-center justify-center bg-danger-500/80 text-white opacity-0 group-hover:opacity-100 transition-opacity">
                                  <TrashIcon className="h-3.5 w-3.5" />
                                </button>
                              </div>
                            ))}
                          </div>
                        </div>
                        <button type="button" onClick={() => removeVariant(i)}
                          className="flex items-center justify-center h-8 w-8 rounded-lg hover:bg-danger-50 text-surface-300 hover:text-danger-500 transition-colors">
                          <TrashIcon className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
            <button type="button" onClick={addVariant}
              className="flex items-center justify-center gap-2 w-full py-2.5 border border-dashed border-surface-200 rounded-xl text-xs font-medium text-surface-500 hover:text-brand-600 hover:border-brand-300 hover:bg-brand-50/30 transition-all">
              <PlusIcon className="h-3.5 w-3.5" /> Add New Variant
            </button>
          </div>
        )}

        {tab === 3 && (
          <>
            <p className="text-xs text-surface-400">First image becomes primary.</p>
            <div className="grid grid-cols-[2fr_1fr_24px] gap-2 pb-1 border-b border-surface-100">
              {['Image File', 'Alt Text', ''].map(h =>
                <span key={h} className="text-xs font-medium text-surface-400">{h}</span>)}
            </div>
            {images.map((img, i) => (
              <div key={i} className="space-y-2">
                <div className="grid grid-cols-[2fr_1fr_24px] gap-2 items-center">
                  <div className="flex items-center gap-2">
                    {img.preview ? (
                      <img src={img.preview} alt="preview"
                        className="h-10 w-10 object-cover rounded flex-shrink-0 border border-surface-200" />
                    ) : img.url ? (
                      <img src={img.url} alt="existing"
                        className="h-10 w-10 object-cover rounded flex-shrink-0 border border-surface-200" />
                    ) : (
                      <div className="h-10 w-10 rounded flex-shrink-0 border border-dashed border-surface-200 flex items-center justify-center text-surface-300 text-xs">?</div>
                    )}
                    <input
                      type="file"
                      accept="image/*"
                      multiple
                      onChange={(e) => {
                        const files = Array.from(e.target.files || []);
                        if (!files.length) return;

                        setImages(prev => {
                          const newImages = files.map(file => ({
                            file,
                            preview: URL.createObjectURL(file),
                            url: '',
                            alt_text: '',
                            is_primary: 0
                          }));

                          const updated = [...prev];

                          // If current row is empty (no url, no file), replace it with the first selected file
                          if (!updated[i].url && !updated[i].file) {
                            updated[i] = { ...updated[i], ...newImages[0] };
                            if (newImages.length > 1) {
                              updated.push(...newImages.slice(1));
                            }
                          } else {
                            // Current row already has an image, just append the new ones
                            updated.push(...newImages);
                          }

                          return updated;
                        });
                      }}
                    />
                  </div>
                  <input
                    className="input text-sm"
                    placeholder="Alt text"
                    value={img.alt_text}
                    onChange={e => {
                      const val = e.target.value;
                      setImages(prev => prev.map((r, j) => j === i ? { ...r, alt_text: val } : r));
                    }}
                  />
                  <button type="button" onClick={() => removeImage(i)}
                    className="text-surface-300 hover:text-danger-500 text-lg leading-none">×</button>
                </div>
                {img.file
                  ? <p className="text-xs text-green-600 ml-1">✓ {img.file.name}</p>
                  : <p className="text-xs text-surface-300 ml-1">No file selected</p>
                }
              </div>
            ))}
            <button type="button" onClick={addImage}
              className="w-full mt-1 py-2 border border-dashed border-surface-200 rounded-lg text-xs text-surface-400 hover:text-surface-600 hover:border-surface-400 transition-colors">
              + Add Image
            </button>
          </>
        )}

        {tab === 4 && (
          <>
            <p className="text-xs text-surface-400">Specs like Processor, RAM, Display size etc.</p>
            {attributes?.length === 0 && (
              <p className="text-sm text-surface-300 text-center py-6">No attributes yet</p>
            )}
            {attributes.map((a, i) => (
              <div key={i} className="grid grid-cols-[1fr_1fr_24px] gap-2 items-center">
                <input className="input text-sm" placeholder="Name (e.g. Processor)" value={a.name}
                  onChange={e => updateAttr(i, 'name', e.target.value)} />
                <input className="input text-sm" placeholder="Value (e.g. Snapdragon 8 Gen 3)" value={a.value}
                  onChange={e => updateAttr(i, 'value', e.target.value)} />
                <button type="button" onClick={() => removeAttr(i)}
                  className="text-surface-300 hover:text-danger-500 text-lg leading-none">×</button>
              </div>
            ))}
            <button type="button" onClick={addAttr}
              className="w-full mt-1 py-2 border border-dashed border-surface-200 rounded-lg text-xs text-surface-400 hover:text-surface-600 hover:border-surface-400 transition-colors">
              + Add Attribute
            </button>
          </>
        )}

        {tab === 5 && (
          <>
            <div className="grid grid-cols-2 gap-3">
              <Input label="Low Stock Alert" type="number" min="0" {...register('low_stock_alert')} />
              <Input label="Weight (grams)" type="number" {...register('weight_grams')} />
            </div>
            <ToggleField label="Track Inventory" hint="Enable stock count tracking"
              checked={toggles.track_inventory} onChange={setToggle('track_inventory')} />

            {variants.length > 0 && (
              <div className="mt-3 border-t border-surface-100 pt-3">
                <p className="text-xs font-medium text-surface-500 uppercase tracking-wide mb-2">Variant Stock</p>
                <div className="grid grid-cols-[2fr_1fr_2fr] gap-2 pb-1 border-b border-surface-100">
                  {['Variant', 'Stock', 'Warehouse'].map(h =>
                    <span key={h} className="text-xs font-medium text-surface-400">{h}</span>)}
                </div>
                {variants.map((v, i) => (
                  <div key={i} className="grid grid-cols-[2fr_1fr_2fr] gap-2 items-center py-1">
                    <span className="text-xs text-surface-600 truncate">{v.name || `Variant ${i + 1}`}</span>
                    <input className="input text-sm" type="number" placeholder="0"
                      value={v._stock_qty ?? ''}
                      onChange={e => updateVariant(i, '_stock_qty', e.target.value)} />
                    <input className="input text-sm" placeholder="RACK-A1"
                      value={v._warehouse_location ?? ''}
                      onChange={e => updateVariant(i, '_warehouse_location', e.target.value)} />
                  </div>
                ))}
              </div>
            )}

            {variants.length === 0 && (
              <div className="grid grid-cols-2 gap-3 mt-2">
                <Input label="Stock Quantity" type="number" min="0" {...register('stock_qty')} />
                <Input label="Reserved Qty" type="number" min="0" {...register('reserved_qty')} />
                <Input label="Warehouse Location" placeholder="RACK-ELEC-A1-S1"
                  className="col-span-2" {...register('warehouse_location')} />
              </div>
            )}
          </>
        )}

        {tab === 6 && (
          <>
            <ToggleField label="Active" hint="Visible to customers"
              checked={toggles.is_active} onChange={setToggle('is_active')} />
            <ToggleField label="Featured" hint="Show in featured sections"
              checked={toggles.is_featured} onChange={setToggle('is_featured')} />
            <ToggleField label="Returnable" hint="Allow return requests"
              checked={toggles.is_returnable} onChange={setToggle('is_returnable')} />

            <div className="grid grid-cols-2 gap-3 pt-2">
              <Input label="Return Window (days)" type="number" {...register('return_window_days')} />
              <Input label="Dimensions (JSON)" placeholder='{"l":16,"w":8,"h":0.8}'
                {...register('dimensions_cm')} />
            </div>

            {selectedType === 'digital' && (
              <Input label="Download URL" type="url" {...register('download_url')} />
            )}
            {selectedType === 'service' && (
              <Input label="Service Duration" placeholder='"1 hour"' {...register('service_duration')} />
            )}
            {selectedType === 'subscription' && (
              <Select label="Subscription Interval" {...register('subscription_interval')}>
                <option value="">— Select —</option>
                {['daily', 'weekly', 'monthly', 'yearly'].map(i =>
                  <option key={i} value={i}>{i}</option>)}
              </Select>
            )}
          </>
        )}
      </div>

      <div className="flex items-center justify-between pt-4 mt-4 border-t border-surface-100">
        <div className="flex gap-2">
          {tab > 0 && (
            <Button type="button" variant="outline" size="sm" onClick={() => setTab(t => t - 1)}>
              ← Back
            </Button>
          )}
          {tab < TABS?.length - 1 && (
            <Button type="button" variant="outline" size="sm" onClick={() => handleTabChange(tab + 1)}>
              Next →
            </Button>
          )}
        </div>

        <Button
          type="submit"
          variant="primary"
          loading={loading}
          disabled={!isValid || !images.some(img => img.file || img.url)}
          className={(!isValid || !images.some(img => img.file || img.url)) ? "opacity-50 cursor-not-allowed" : ""}
        >
          {product ? 'Save Changes' : 'Create Product'}
        </Button>
      </div>
    </form>
  );
};

export default function ProductsPage({ business }) {
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [priceMin, setPriceMin] = useState('');
  const [priceMax, setPriceMax] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editProduct, setEditProduct] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const { page, setPage, reset } = usePagination();
  const dSearch = useDebounce(search);

  const { data, loading, refetch } = useQuery(
    () => productsAPI.list({
      page,
      limit: 20,
      business_id: business?.id,
      search: dSearch || undefined,
      type: typeFilter || undefined,
      product_category: categoryFilter || undefined,
      min_price: priceMin || undefined,
      max_price: priceMax || undefined,
    }),
    [page, dSearch, typeFilter, categoryFilter, priceMin, priceMax, business?.id]
  );

  const { data: catData } = useQuery(async () => {
    try {
      const res = await categoriesAPI.list({ limit: 2000, all: 'true' });
      const flat = normalizeCategories(res?.data || res);
      if (flat.length > 0) return flat;
    } catch (e) {
      console.warn('Error fetching categories list:', e);
    }
    try {
      const treeRes = await categoriesAPI.tree();
      return normalizeCategories(treeRes?.data || treeRes);
    } catch (e) {
      return [];
    }
  }, []);

  const { mutate: createProduct, loading: creating } = useMutation(
    (d) => productsAPI.createV2(d),
    { successMsg: 'Product created', onSuccess: () => { refetch(); setShowModal(false); } }
  );
  const { mutate: updateProduct, loading: updating } = useMutation(
    (d) => productsAPI.update(editProduct?.id, d),
    { successMsg: 'Product updated', onSuccess: async () => { await refetch(); setEditProduct(null); } }
  );
  const { mutate: deleteProduct, loading: deleting } = useMutation(
    () => productsAPI.delete(deleteTarget?.id),
    { successMsg: 'Product deleted', onSuccess: () => { refetch(); setDeleteTarget(null); } }
  );

  const products = data?.data?.rows || data?.rows || [];
  const total = data?.data?.count || data?.count || 0;
  const pages = Math.ceil(total / 20) || 1;
  const priceFilter = data?.data?.price_filter || data?.price_filter || null;

  const cats = useMemo(() => normalizeCategories(catData), [catData]);

  const applyBracket = (bracket) => {
    if (priceMin === String(bracket.min) && priceMax === String(bracket.max)) {
      // toggle off
      setPriceMin(''); setPriceMax(''); reset();
    } else {
      setPriceMin(String(bracket.min)); setPriceMax(String(bracket.max)); reset();
    }
  };

  return (
    <div className="animate-in space-y-5">
      <SectionHeader
        title="Products"
        subtitle={`${total} products`}
        action={
          <Button variant="primary" size="sm" icon={PlusIcon} onClick={() => setShowModal(true)}>
            Add Product
          </Button>
        }
      />

      <Card>
        <div className="flex flex-wrap items-center gap-3 mb-5">
          <SearchInput
            value={search}
            onChange={v => { setSearch(v); reset(); }}
            className="flex-1 min-w-[200px]"
            placeholder="Search products..."
          />

          {/* Product Category filter */}
          <select
            className="input w-40"
            value={categoryFilter}
            onChange={e => { setCategoryFilter(e.target.value); reset(); }}
          >
            <option value="">All Categories</option>
            {PRODUCT_CATEGORIES.map(c => (
              <option key={c} value={c} className="capitalize">{c}</option>
            ))}
          </select>

          {/* Product Type filter */}
          <select
            className="input w-40"
            value={typeFilter}
            onChange={e => { setTypeFilter(e.target.value); reset(); }}
          >
            <option value="">All Types</option>
            {PRODUCT_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
          </select>

          {/* Price Min-Max manual inputs */}
          <div className="flex items-center gap-1">
            <input
              type="number" min="0" placeholder="Min ₹"
              className="input w-24 text-sm"
              value={priceMin}
              onChange={e => { setPriceMin(e.target.value); reset(); }}
            />
            <span className="text-surface-400 text-xs">—</span>
            <input
              type="number" min="0" placeholder="Max ₹"
              className="input w-24 text-sm"
              value={priceMax}
              onChange={e => { setPriceMax(e.target.value); reset(); }}
            />
          </div>

          {/* Clear price filter */}
          {(priceMin || priceMax) && (
            <button
              type="button"
              onClick={() => { setPriceMin(''); setPriceMax(''); reset(); }}
              className="text-xs text-danger-500 hover:underline"
            >
              Clear Price
            </button>
          )}
        </div>

        {/* Dynamic price bracket chips from backend */}
        {priceFilter?.brackets?.length > 0 && (
          <div className="flex flex-wrap gap-2 mb-4">
            <span className="text-xs text-surface-400 self-center">Quick:</span>
            {priceFilter.brackets.map((b, idx) => {
              const isActive = priceMin === String(b.min) && priceMax === String(b.max);
              return (
                <button
                  key={idx}
                  type="button"
                  onClick={() => applyBracket(b)}
                  className={`px-3 py-1 rounded-full text-xs border transition-colors ${
                    isActive
                      ? 'bg-surface-900 text-white border-surface-900'
                      : 'border-surface-200 text-surface-500 hover:border-surface-400 hover:text-surface-700'
                  }`}
                >
                  {b.label}
                </button>
              );
            })}
          </div>
        )}

        {loading ? <LoadingPage /> : products.length === 0 ? (
          <EmptyState
            icon={CubeIcon}
            title="No products found"
            action={
              <Button variant="primary" size="sm" icon={PlusIcon} onClick={() => setShowModal(true)}>
                Add First Product
              </Button>
            }
          />
        ) : (
          <div className="overflow-x-auto">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Product</th>
                  <th>Category</th>
                  <th>Type</th>
                  <th>Price</th>
                  <th>Stock</th>
                  <th>Status</th>
                  <th>Created</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {products.map(p => (
                  <tr key={p.id}>
                    <td>
                      <div className="flex items-center gap-3">
                        {(p.primary_image?.url || p.images?.[0]?.url)
                          ? <img
                            src={p.primary_image?.url || p.images?.[0]?.url}
                            alt={p.name}
                            className="h-10 w-10 rounded-lg object-cover flex-shrink-0"
                          />
                          : <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg bg-surface-100">
                            <CubeIcon className="h-5 w-5 text-surface-400" />
                          </div>
                        }
                        <div>
                          <p className="font-medium text-surface-900 max-w-[180px] truncate">{p.name}</p>
                          <p className="text-xs text-surface-400">{p.sku || p.brand || '—'}</p>
                        </div>
                      </div>
                    </td>

                    <td>
                      <Badge variant={p.product_category === 'promo' ? 'warning' : 'gray'}>
                        {p.product_category || 'standard'}
                      </Badge>
                    </td>

                    <td><Badge variant="gray">{p.product_type}</Badge></td>

                    <td>
                      <span className="font-mono font-semibold text-surface-900">
                        ₹{Number(p.sale_price || p.base_price).toLocaleString()}
                      </span>
                      {p.sale_price && Number(p.sale_price) < Number(p.base_price) && (
                        <p className="text-xs text-surface-400 line-through">
                          ₹{Number(p.base_price).toLocaleString()}
                        </p>
                      )}
                    </td>

                    <td>
                      {p.track_inventory ? (
                        <span className={`font-mono text-sm font-medium ${(p.inventory?.stock_qty ?? p.stock_qty ?? 0) <= (p.low_stock_alert || 5)
                          ? 'text-danger-600' : 'text-surface-700'
                          }`}>
                          {p.inventory?.stock_qty ?? p.stock_qty ?? 0}
                          {(p.inventory?.stock_qty ?? p.stock_qty ?? 0) <= (p.low_stock_alert || 5) &&
                            <span className="ml-1 text-xs">⚠️</span>
                          }
                        </span>
                      ) : (
                        <span className="text-surface-400 text-xs">Unlimited</span>
                      )}
                    </td>

                    <td><StatusBadge status={p.is_active ? 'active' : 'suspended'} /></td>

                    <td className="text-surface-400 text-xs">
                      {p.created_at ? format(new Date(p.created_at), 'dd MMM yy') : '—'}
                    </td>

                    <td>
                      <div className="flex items-center gap-1">
                        <button
                          className="btn btn-ghost p-1.5 rounded-lg"
                          onClick={() => {
                            productsAPI.get(p.slug)
                              .then(res => {
                                const productData = res?.data?.data || res?.data || res;
                                setEditProduct(productData);
                              })
                              .catch(() => {
                                // Fallback to list data if slug fetch fails
                                setEditProduct(p);
                              });
                          }}
                          title="Edit"
                        >
                          <PencilSquareIcon className="h-4 w-4" />
                        </button>
                        <button
                          className="btn btn-ghost p-1.5 rounded-lg text-danger-500"
                          onClick={() => setDeleteTarget(p)}
                          title="Delete"
                        >
                          <TrashIcon className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <Pagination page={page} pages={pages} onPageChange={setPage} />
          </div>
        )}
      </Card>

      <Modal open={showModal} onClose={() => setShowModal(false)} title="Add Product" size="full">
        <ProductForm categories={cats} onSubmit={createProduct} loading={creating} business={business} />
      </Modal>

      <Modal open={!!editProduct} onClose={() => setEditProduct(null)} title="Edit Product" size="full">
        <ProductForm product={editProduct} categories={cats} onSubmit={updateProduct} loading={updating} business={business} />
      </Modal>

      <ConfirmDialog
        open={!!deleteTarget} danger
        title="Delete Product"
        message={`Delete "${deleteTarget?.name}"? This cannot be undone.`}
        confirmText="Delete" loading={deleting}
        onClose={() => setDeleteTarget(null)} onConfirm={deleteProduct}
      />
    </div>
  );
}